import React, { useState } from "react";
import type { ReactElement, MouseEvent } from "react";
import {
  Box,
  Stack,
  Tooltip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomTruncateText } from "@ippon/shared-components";

// Type definitions
export interface HeaderMenuItem {
  title: string;
  link?: string;
  action?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  divider?: boolean;
}

export interface HeaderUser {
  id?: string;
  name?: string;
  email?: string;
  avatar?: string;
  initials?: string;
}

export interface HeaderTheme {
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  height?: number | string;
  profileButtonStyle?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
}

export interface HeaderProps {
  // User information
  user?: HeaderUser;
  fullName?: string;
  employeeId?: string;

  // Breadcrumb component
  breadcrumb?:
    | React.ReactNode
    | ((data: {
        propertiesData?: any;
        inquiryData?: any;
        propertiesInquiriesData?: any;
        employeeData?: any;
      }) => React.ReactNode);

  // Data props for breadcrumb
  propertiesData?: any;
  inquiryData?: any;
  propertiesInquiriesData?: any;
  employeeData?: any;

  // Menu configuration
  menuItems?: HeaderMenuItem[];
  defaultMenuItems?: boolean;

  // Navigation
  onNavigate?: (path: string) => void;
  LinkComponent?: React.ComponentType<any>;

  // Actions
  onLogout?: () => void;
  onProfileClick?: () => void;

  // Customization
  theme?: HeaderTheme;
  logo?: React.ReactNode;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  centerContent?: React.ReactNode;
  profileImage?: string;

  // Layout
  showUserMenu?: boolean;
  showBreadcrumb?: boolean;
  fixed?: boolean;
  elevation?: number;

  // Custom styles
  containerStyle?: React.CSSProperties;

  // Callbacks
  onMenuItemClick?: (item: HeaderMenuItem) => void;
}

// Default theme
const defaultTheme: Required<HeaderTheme> = {
  backgroundColor: "#FFFFFF",
  textColor: "#6E778A",
  borderColor: "#e0e0e0",
  height: 50,
  profileButtonStyle: {},
  menuStyle: {},
};

// Merge themes
const mergeTheme = (custom?: HeaderTheme): Required<HeaderTheme> => ({
  ...defaultTheme,
  ...custom,
  profileButtonStyle: {
    ...defaultTheme.profileButtonStyle,
    ...custom?.profileButtonStyle,
  },
  menuStyle: {
    ...defaultTheme.menuStyle,
    ...custom?.menuStyle,
  },
});

// Default menu items
const getDefaultMenuItems = (userId?: string): HeaderMenuItem[] => [
  {
    title: "プロフィール",
    link: userId ? `/setting/account/${userId}` : "/setting/account",
    icon: null,
  },
  {
    title: "ログアウト",
    action: "logout",
    icon: null,
    divider: true,
  },
];

export const Header: React.FC<HeaderProps> = ({
  user,
  fullName,
  employeeId,
  breadcrumb,
  propertiesData,
  inquiryData,
  propertiesInquiriesData,
  employeeData,
  menuItems,
  defaultMenuItems = true,
  onNavigate,
  onLogout,
  onProfileClick,
  theme,
  logo,
  leftContent,
  rightContent,
  centerContent,
  profileImage,
  showUserMenu = true,
  showBreadcrumb = true,
  fixed = false,
  elevation = 0,
  containerStyle,
  onMenuItemClick,
}): ReactElement => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const mergedTheme = mergeTheme(theme);
  const muiTheme = useTheme();
  // Header 自体は sm 以上で表示されるため、電話幅のみ名前を隠す。
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  // Get user name from various sources
  const userName = fullName || user?.name || "";
  const userId = employeeId || user?.id;

  // Get menu items
  const finalMenuItems =
    menuItems || (defaultMenuItems ? getDefaultMenuItems(userId) : []);

  // Default Link component if none provided
  // const Link =
  //   LinkComponent ||
  //   (({ to, children, style }: any) => (
  //     <a
  //       href={to}
  //       style={style}
  //       onClick={(e) => {
  //         e.preventDefault();
  //         if (onNavigate) {
  //           onNavigate(to);
  //         }
  //       }}
  //     >
  //       {children}
  //     </a>
  //   ));

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuItemClick = async (item: HeaderMenuItem) => {
    if (item.action === "logout") {
      if (onLogout) {
        await onLogout();
      }
      if (onNavigate) {
        onNavigate("/login");
      }
    } else if (item.link && item.link !== "#") {
      // 一時的なアンカー要素を作成してナビゲーションをトリガーする
      const anchor = document.createElement("a");
      anchor.href = item.link;
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }

    if (item.onClick) {
      item.onClick();
    }

    if (onMenuItemClick) {
      onMenuItemClick(item);
    }

    handleCloseUserMenu();
  };

  const handleProfileClick = (event: MouseEvent<HTMLElement>) => {
    if (onProfileClick) {
      onProfileClick();
    } else {
      handleOpenUserMenu(event);
    }
  };

  // Get user initials if no avatar
  const getUserInitials = () => {
    if (user?.initials) return user.initials;
    if (userName) {
      const names = userName.split(" ");
      return names
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "U";
  };

  // Get avatar source
  const avatarSrc = profileImage || user?.avatar || "";

  const styles = {
    container: {
      height: mergedTheme.height,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: mergedTheme.backgroundColor,
      ...(fixed && {
        position: "fixed" as const,
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
      }),
      ...(elevation > 0 && {
        boxShadow: elevation,
      }),
      ...containerStyle,
    },
    profileButton: {
      p: 0.5,
      mr: 0.6,
      border: `1px solid ${mergedTheme.borderColor}`,
      ...mergedTheme.profileButtonStyle,
    },
    profileText: {
      color: mergedTheme.textColor,
      fontSize: 14,
      fontWeight: "bold",
      borderBottom: `0.6px solid ${mergedTheme.textColor}`,
      display: "inline-block",
    },
    menu: {
      mt: "45px",
      ...mergedTheme.menuStyle,
    },
  };

  // Process breadcrumb with data if it's a function
  const processedBreadcrumb =
    typeof breadcrumb === "function"
      ? breadcrumb({
          propertiesData,
          inquiryData,
          propertiesInquiriesData,
          employeeData,
        })
      : breadcrumb;

  return (
    <>
      <Box sx={styles.container}>
        {/* Left Section - Breadcrumb */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {logo && <Box sx={{ mr: 2, flexShrink: 0 }}>{logo}</Box>}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {leftContent ? leftContent : showBreadcrumb && processedBreadcrumb}
          </Box>
        </Box>

        {/* Center Section */}
        {centerContent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 0,
            }}
          >
            {centerContent}
          </Box>
        )}

        {/* Right Section - User Menu */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {rightContent
            ? rightContent
            : showUserMenu && (
                <Stack
                  direction="row"
                  alignItems="center"
                  width={rightContent ? "100%" : "auto"}
                  justifyContent="flex-end"
                >
                  <Tooltip title="プロフィールを開く">
                    <Stack
                      direction="row"
                      alignItems="center"
                      onClick={handleProfileClick}
                      sx={{ cursor: "pointer", minWidth: "91px" }}
                    >
                      <IconButton sx={styles.profileButton}>
                        <Avatar
                          alt={userName || ""}
                          src={avatarSrc}
                          sx={{ width: 25, height: 25 }}
                        >
                          {!avatarSrc && getUserInitials()}
                        </Avatar>
                      </IconButton>
                      {userName && !isMobile && (
                        <Box
                          sx={{
                            ...styles.profileText,
                            maxWidth: "200px",
                            minWidth: 0,
                          }}
                        >
                          <CustomTruncateText text={userName} />
                        </Box>
                      )}
                    </Stack>
                  </Tooltip>
                </Stack>
              )}
        </Box>

        {/* User Menu */}
        {showUserMenu && (
          <Menu
            sx={styles.menu}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {finalMenuItems.map((item, index) => (
              <div key={index}>
                {item.divider && index > 0 && (
                  <Box sx={{ borderTop: "1px solid #e0e0e0", my: 1 }} />
                )}
                <MenuItem onClick={() => handleMenuItemClick(item)}>
                  {item.icon && (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      {item.icon}
                    </Box>
                  )}
                  <Typography sx={{ textAlign: "center", fontSize: 14 }}>
                    {item.title}
                  </Typography>
                </MenuItem>
              </div>
            ))}
          </Menu>
        )}
      </Box>
      {/* Spacer for fixed header */}
      {fixed && <Box sx={{ height: mergedTheme.height }} />}
    </>
  );
};

// Export a composed version with Breadcrumb for convenience
export { Header as default };
